import { useState, useEffect, useMemo } from "react";
import { authFetch } from "../App";
import {
  format, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, startOfYear, endOfYear,
} from "date-fns";
import { de } from "date-fns/locale";
import {
  Clock, Car, TrendingUp, TrendingDown, Palmtree, Thermometer, Users, BarChart3,
  ArrowUp, ArrowDown, Minus, ChevronRight,
} from "lucide-react";

const API = "/api";
const hm = (m) => {
  const v = Math.abs(m || 0);
  return `${Math.floor(v / 60)}:${(v % 60).toString().padStart(2, "0")}`;
};

const PERIODS = [
  { key: "current", label: "Aktueller Monat" },
  { key: "last", label: "Letzter Monat" },
  { key: "quarter", label: "Quartal" },
  { key: "year", label: "Jahr" },
  { key: "custom", label: "Zeitraum" },
];

function getDateRange(periodKey, customFrom, customTo) {
  const now = new Date();
  switch (periodKey) {
    case "current": return [startOfMonth(now), endOfMonth(now)];
    case "last": { const p = subMonths(now, 1); return [startOfMonth(p), endOfMonth(p)]; }
    case "quarter": return [startOfQuarter(now), endOfQuarter(now)];
    case "year": return [startOfYear(now), endOfYear(now)];
    case "custom": return [customFrom ? new Date(customFrom) : startOfMonth(now), customTo ? new Date(customTo) : endOfMonth(now)];
    default: return [startOfMonth(now), endOfMonth(now)];
  }
}

/* ── Color System ── */
const KPI_THEMES = {
  work:    { border: "var(--tga-blue)", icon: "#60A5FA", iconBg: "var(--tga-blue-soft)", bg: "var(--tga-blue-soft)" },
  travel:  { border: "#8B5CF6", icon: "#A78BFA", iconBg: "var(--tga-purple-soft)", bg: "var(--tga-purple-soft)" },
  plus:    { border: "#10B981", icon: "#34D399", iconBg: "var(--tga-green-soft)", bg: "var(--tga-green-soft)" },
  minus:   { border: "#EF4444", icon: "#F87171", iconBg: "var(--tga-red-soft)", bg: "var(--tga-red-soft)" },
  neutral: { border: "var(--tga-text-muted)", icon: "var(--tga-text-secondary)", iconBg: "var(--tga-bg-content)", bg: "var(--tga-bg-card)" },
  urlaub:  { border: "#F59E0B", icon: "#FBBF24", iconBg: "var(--tga-amber-soft)", bg: "var(--tga-amber-soft)" },
  krank:   { border: "#F43F5E", icon: "#FB7185", iconBg: "var(--tga-red-soft)", bg: "var(--tga-red-soft)" },
};

function KpiCard({ icon: Icon, label, value, sub, theme, trend }) {
  const t = KPI_THEMES[theme] || KPI_THEMES.neutral;
  return (
    <div
      className="rounded-xl relative overflow-hidden transition-all duration-200"
      style={{
        background: t.bg,
        border: `1px solid var(--tga-border)`,
        borderLeft: `4px solid ${t.border}`,
        boxShadow: "var(--tga-shadow-card)",
      }}
      data-testid={`kpi-${label.toLowerCase().replace(/[\s\/]/g, "-")}`}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = `var(--tga-shadow-md)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "var(--tga-shadow-card)";
      }}
    >
      <div className="p-4">
        <div className="flex items-center gap-2.5 mb-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: t.iconBg }}
          >
            <Icon className="w-[18px] h-[18px]" style={{ color: t.icon }} strokeWidth={2.2} />
          </div>
          <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--tga-text-muted)" }}>
            {label}
          </span>
          {trend !== undefined && trend !== null && (
            <div className="ml-auto flex items-center gap-0.5">
              {trend > 0 ? (
                <ArrowUp className="w-3 h-3" style={{ color: "#34D399" }} />
              ) : trend < 0 ? (
                <ArrowDown className="w-3 h-3" style={{ color: "#F87171" }} />
              ) : (
                <Minus className="w-3 h-3" style={{ color: "var(--tga-text-muted)" }} />
              )}
            </div>
          )}
        </div>
        <p
          className="text-[26px] font-black tracking-tight font-mono"
          style={{ color: t.icon, lineHeight: 1.1 }}
        >
          {value}
        </p>
        {sub && (
          <p className="text-[11px] mt-1.5 font-medium" style={{ color: "var(--tga-text-dim)" }}>
            {sub}
          </p>
        )}
      </div>
    </div>
  );
}

/* ── Mini Progress Bar ── */
function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 120) : 0;
  return (
    <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--tga-border-strong)" }}>
      <div
        className="h-full rounded-full transition-all duration-300"
        style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color || "var(--tga-blue)" }}
      />
    </div>
  );
}

export default function StundenkontenDashboard({ users, onSelectEmployee }) {
  const [period, setPeriod] = useState("current");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [fromDate, toDate] = useMemo(() => {
    const [f, t] = getDateRange(period, customFrom, customTo);
    return [format(f, "yyyy-MM-dd"), format(t, "yyyy-MM-dd")];
  }, [period, customFrom, customTo]);

  const periodLabel = useMemo(() => {
    const [f, t] = getDateRange(period, customFrom, customTo);
    if (period === "current" || period === "last") return format(f, "MMMM yyyy", { locale: de });
    if (period === "quarter") return `Q${Math.ceil((f.getMonth() + 1) / 3)} ${f.getFullYear()}`;
    if (period === "year") return `Jahr ${f.getFullYear()}`;
    return `${format(f, "dd.MM.yyyy")} – ${format(t, "dd.MM.yyyy")}`;
  }, [period, customFrom, customTo]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await authFetch(`${API}/timesheet/dashboard?from=${fromDate}&to=${toDate}`);
        if (res.ok) setData(await res.json());
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [fromDate, toDate]);

  const t = data?.totals || {};
  const diffMin = t.diff_minutes || 0;
  const maxWork = Math.max(...(data?.employees || []).map(e => e.work_minutes || 0), 1);

  return (
    <div className="space-y-5 pb-8" data-testid="stundenkonten-dashboard">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-bold tracking-tight" style={{ color: "var(--tga-text-bright)" }}>
            {periodLabel}
          </h2>
          <p className="text-[11px]" style={{ color: "var(--tga-text-dim)" }}>Stundenkonten Uebersicht</p>
        </div>
      </div>

      {/* ── Period Selector ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div
          className="flex rounded-lg overflow-hidden"
          style={{ border: "1px solid var(--tga-border)", background: "var(--tga-bg-app)" }}
          data-testid="period-selector"
        >
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className="px-3.5 py-2 text-[12px] font-semibold transition-all duration-150"
              style={{
                background: period === p.key ? "var(--tga-blue)" : "transparent",
                color: period === p.key ? "#FFFFFF" : "var(--tga-text-muted)",
                borderRight: "1px solid var(--tga-border)",
              }}
              onMouseEnter={(e) => { if (period !== p.key) e.currentTarget.style.background = "var(--tga-bg-hover)"; }}
              onMouseLeave={(e) => { if (period !== p.key) e.currentTarget.style.background = "transparent"; }}
              data-testid={`period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-center gap-2">
            <input
              type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
              className="h-8 text-[12px] rounded-md px-2.5 focus:outline-none"
              style={{ background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)", color: "var(--tga-text-primary)" }}
              data-testid="custom-from"
            />
            <span className="text-[11px]" style={{ color: "var(--tga-text-dim)" }}>bis</span>
            <input
              type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
              className="h-8 text-[12px] rounded-md px-2.5 focus:outline-none"
              style={{ background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)", color: "var(--tga-text-primary)" }}
              data-testid="custom-to"
            />
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-7 w-7 border-[3px] border-t-transparent" style={{ borderColor: "var(--tga-blue)", borderTopColor: "transparent" }} />
            <span className="text-[12px]" style={{ color: "var(--tga-text-dim)" }}>Lade Stundendaten...</span>
          </div>
        </div>
      ) : (
        <>
          {/* ── KPI Cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <KpiCard
              icon={Clock} label="Arbeitsstunden" theme="work"
              value={hm(t.work_minutes)}
              sub={`Soll: ${hm(t.soll_minutes)}`}
            />
            <KpiCard
              icon={Car} label="Fahrtstunden" theme="travel"
              value={hm(t.travel_minutes)}
            />
            <KpiCard
              icon={diffMin >= 0 ? TrendingUp : TrendingDown}
              label="Ueberstunden"
              theme={diffMin > 0 ? "plus" : diffMin < 0 ? "minus" : "neutral"}
              value={`${diffMin >= 0 ? "+" : "-"}${hm(diffMin)}`}
              sub={diffMin >= 0 ? "Gesamt Plusstunden" : "Gesamt Minusstunden"}
            />
            <KpiCard
              icon={Palmtree} label="Urlaubstage" theme="urlaub"
              value={t.urlaub_days || 0}
            />
            <KpiCard
              icon={Thermometer} label="Krankheitstage" theme="krank"
              value={t.krank_days || 0}
            />
          </div>

          {/* ── Employee Table ── */}
          <div
            className="rounded-xl overflow-hidden"
            style={{
              background: "var(--tga-bg-card)",
              border: "1px solid var(--tga-border)",
              boxShadow: "var(--tga-shadow-lg)",
            }}
            data-testid="employee-comparison"
          >
            <div
              className="flex items-center gap-2.5 px-5 py-3.5"
              style={{ background: "var(--tga-bg-elevated)", borderBottom: "1px solid var(--tga-border)" }}
            >
              <BarChart3 className="w-4 h-4" style={{ color: "var(--tga-blue)" }} />
              <h3 className="text-[13px] font-bold" style={{ color: "var(--tga-text-primary)" }}>Mitarbeitervergleich</h3>
              <span className="text-[11px] ml-1" style={{ color: "var(--tga-text-dim)" }}>{data?.employees?.length || 0} MA</span>
            </div>

            <table className="w-full" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--tga-bg-elevated)", borderBottom: "2px solid var(--tga-border)" }}>
                  <th className="text-left text-[10px] font-bold uppercase tracking-wider px-5 py-2.5" style={{ color: "var(--tga-text-dim)" }}>
                    Mitarbeiter
                  </th>
                  <th className="text-right text-[10px] font-bold uppercase tracking-wider px-4 py-2.5" style={{ color: "var(--tga-blue)" }}>
                    Arbeit
                  </th>
                  <th className="text-right text-[10px] font-bold uppercase tracking-wider px-4 py-2.5" style={{ color: "#8B5CF6" }}>
                    Fahrt
                  </th>
                  <th className="text-right text-[10px] font-bold uppercase tracking-wider px-4 py-2.5" style={{ color: "var(--tga-text-dim)" }}>
                    Soll
                  </th>
                  <th className="text-right text-[10px] font-bold uppercase tracking-wider px-4 py-2.5" style={{ color: "var(--tga-text-dim)" }}>
                    Ueberstunden
                  </th>
                  <th className="text-right text-[10px] font-bold uppercase tracking-wider px-4 py-2.5" style={{ color: "#F59E0B" }}>
                    Urlaub
                  </th>
                  <th className="text-right text-[10px] font-bold uppercase tracking-wider px-5 py-2.5" style={{ color: "#F43F5E" }}>
                    Krank
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data?.employees || []).map((emp, i) => {
                  const diff = emp.diff_minutes || 0;
                  const diffColor = diff > 0 ? "#34D399" : diff < 0 ? "#F87171" : "var(--tga-text-muted)";
                  const diffIcon = diff > 0 ? ArrowUp : diff < 0 ? ArrowDown : Minus;
                  const DiffIcon = diffIcon;

                  return (
                    <tr
                      key={emp.id}
                      onClick={() => onSelectEmployee(emp.id)}
                      className="cursor-pointer transition-colors duration-100"
                      style={{
                        background: "var(--tga-bg-card)",
                        borderBottom: "1px solid var(--tga-border)",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--tga-bg-content)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "var(--tga-bg-card)")}
                      data-testid={`emp-row-${emp.id}`}
                    >
                      {/* Name + mini bar */}
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold"
                            style={{ background: "var(--tga-bg-content)", color: "var(--tga-text-secondary)", border: "1px solid var(--tga-border)" }}
                          >
                            {emp.name ? emp.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "?"}
                          </div>
                          <div>
                            <span className="text-[13px] font-semibold block" style={{ color: "var(--tga-text-bright)" }}>
                              {emp.name}
                            </span>
                            <MiniBar value={emp.work_minutes || 0} max={maxWork} color="var(--tga-blue)" />
                          </div>
                        </div>
                      </td>

                      {/* Arbeitsstunden - Blau */}
                      <td className="text-right px-4 py-3">
                        <span className="text-[14px] font-mono font-bold" style={{ color: "#60A5FA" }}>
                          {hm(emp.work_minutes)}
                        </span>
                      </td>

                      {/* Fahrtstunden - Lila */}
                      <td className="text-right px-4 py-3">
                        <span className="text-[13px] font-mono font-medium" style={{ color: "#A78BFA" }}>
                          {hm(emp.travel_minutes)}
                        </span>
                      </td>

                      {/* Soll - Grau */}
                      <td className="text-right px-4 py-3">
                        <span className="text-[13px] font-mono" style={{ color: "var(--tga-text-dim)" }}>
                          {hm(emp.soll_minutes)}
                        </span>
                      </td>

                      {/* Ueberstunden - Gruen/Rot/Grau */}
                      <td className="text-right px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <DiffIcon className="w-3 h-3" style={{ color: diffColor }} />
                          <span
                            className="text-[14px] font-mono font-black"
                            style={{ color: diffColor }}
                          >
                            {diff >= 0 ? "+" : "-"}{hm(diff)}
                          </span>
                        </div>
                      </td>

                      {/* Urlaub - Gelb */}
                      <td className="text-right px-4 py-3">
                        {emp.urlaub_days ? (
                          <span className="text-[13px] font-semibold" style={{ color: "#FBBF24" }}>
                            {emp.urlaub_days}
                          </span>
                        ) : (
                          <span style={{ color: "var(--tga-bg-active)" }}>&ndash;</span>
                        )}
                      </td>

                      {/* Krank - Rosa */}
                      <td className="text-right px-5 py-3">
                        {emp.krank_days ? (
                          <span className="text-[13px] font-semibold" style={{ color: "#FB7185" }}>
                            {emp.krank_days}
                          </span>
                        ) : (
                          <span style={{ color: "var(--tga-bg-active)" }}>&ndash;</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {(data?.employees || []).length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-12">
                      <Users className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--tga-bg-active)" }} />
                      <p className="text-[12px]" style={{ color: "var(--tga-text-dim)" }}>Keine Daten vorhanden</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
